import dialog from './dialog'
import capacitor from './capacitor'
import base64 from './base64'
import tutorial from './tutorial'
import ErrorModal from '@/components/Modal/Error'
import FileViewerModal from '@/components/Modal/FileViewer'

const DEFAULT_MAX_FILE_SIZE = 20 * 1024 * 1024 // 20 MB

function dataUrlSize (dataUrl) {
  const payload = dataUrl.split(',')[1] || ''
  return Math.ceil(payload.length * 3 / 4)
}

function formatMb (bytes) {
  return Math.round(bytes / (1024 * 1024))
}

const sources = {
  camera: {
    label: 'Bild mit Kamera aufnehmen',
    use: o => fromCamera(o)
  },
  picker: {
    label: 'Datei aus Speicher wählen',
    use: o => fromPicker(o)
  },
  imagePicker: {
    label: 'Bild aus Speicher wählen',
    use: o => fromImagePicker(o)
  },
  filePicker: {
    label: 'Dateien auswählen',
    use: o => fromFilePicker(o)
  }
}

async function openPicker(options, capture) {
  return new Promise((resolve) => {
    const types = options?.types ? options.types : [];
    const maxSize = options?.maxSize ?? DEFAULT_MAX_FILE_SIZE;
    const input = document.createElement('input');

    if (capture) {
      input.capture = 'environment';
    }

    input.type = 'file';
    input.multiple = options.mode === 'multiple';
    input.accept = types.join(',');
    input.style.width = 0;

    input.addEventListener('change', async () => {
      let accepted = [];
      let notAccepted = [];
      let tooLarge = [];

      for (let i = 0; i < input.files.length; ++i) {
        let file = input.files[i];

        if (file.size > maxSize) {
          tooLarge.push(file.name);
        } else if (types.indexOf(file.type) >= 0) {
          accepted.push({
            type: file.type,
            name: file.name,
            data: file.type === 'image/jpeg' && options.size ? await resize(file, options.size) : await base64.toDataUrl(file),
            source: capture ? 'camera' : 'picker'
          });
        } else {
          notAccepted.push(file.name);
        }
      }

      if (tooLarge.length > 0) {
        dialog.component(ErrorModal, {
          title: 'Datei zu groß',
          message: `Einige Dateien überschreiten die maximale Dateigröße von ${formatMb(maxSize)} MB und wurden ignoriert: ` + tooLarge.join(', ')
        });
      }

      if (notAccepted.length > 0) {
        dialog.component(ErrorModal, {
          title: 'Falscher Dateityp',
          message: 'Einige Dateien wurden aufgrund des falschen Dateityps ignoriert: ' + notAccepted.join(', ')
        });
      }

      document.body.removeChild(input);
      resolve(accepted);
    });

    document.body.appendChild(input);

    input.focus();
    // input.addEventListener('focus', event => {
    //   setTimeout(() => {
    //     if (event.target.files.length < 1) {
    //       resolve([]);
    //     }
    //   }, 500);
    // });

    input.click();
  });
}

async function requestPhoto (options) {
  const maxSize = options?.maxSize ?? DEFAULT_MAX_FILE_SIZE
  const data = await capacitor.request('camera', { width: options?.size, height: options?.size })

  if (dataUrlSize(data) > maxSize) {
    dialog.component(ErrorModal, {
      title: 'Datei zu groß',
      message: `Das aufgenommene Bild überschreitet die maximale Dateigröße von ${formatMb(maxSize)} MB.`
    })
    return []
  }

  return [
    {
      type: 'image/jpeg',
      name: 'Bild',
      data,
      source: 'camera'
    }
  ]
}

async function resize (source, size = 1000, type = 'image/jpeg') {
  return new Promise((resolve, reject) => {
    const image = new Image()
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    try {
      image.onload = event => {
        let scale = 1

        if (image.width > image.height) {
          scale = size / image.width
        } else {
          scale = size / image.height
        }

        if (scale < 1) {
          canvas.height = image.height * scale
          canvas.width = image.width * scale

          ctx.drawImage(event.target, 0, 0, canvas.width, canvas.height)

          resolve(canvas.toDataURL(type))
        } else {
          resolve(source instanceof File ? base64.toDataUrl(source) : source)
        }
      }

      image.src = source instanceof File ? URL.createObjectURL(source) : source
    } catch {
      reject()
    }
  })
}

async function choose (options, sourceList) {
  try {
    const source = sourceList.length === 1 ? sourceList[0] : await dialog.action('Bitte wählen:', 'Abbrechen', sourceList)

    if (options.onSelect instanceof Function) {
      options.onSelect(source)
    }

    return source && source.use instanceof Function ? await source.use(options) : false
  } catch {
    return false
  }
}

async function fromCamera (options = {}) {
  options.types = ['image/jpeg']

  if (!options.skipTutorial) await tutorial.check('capture')

  if (capacitor.check()) {
    return requestPhoto(options)
  } else {
    return openPicker(options, true)
  }
}

async function fromPicker (options = {}) {
  return openPicker(options);
}

async function fromImagePicker (options = {}) {
  options.types = ['image/jpeg']

  return openPicker(options)
}

async function fromFilePicker (options = {}) {
  return openPicker(options)
}

function view (name, content) {
  dialog.component(FileViewerModal, {name, content}, true)
}

export default {
  sources,
  choose,
  fromCamera,
  fromPicker,
  view,
  resize
}